import { useMemo, useState } from "react";
import { ExternalLink, FileText, Image as ImageIcon, Paperclip } from "lucide-react";
import {
  FOGBUGZ_TO_ADO_SEVERITIES,
  FOGBUGZ_TO_ADO_TEXT_SECTION_MAX,
  FOGBUGZ_TO_ADO_TITLE_MAX,
  composeFogbugzToAdoBug,
  fogbugzToAdoCreateBodySchema,
  type FogbugzToAdoAttachmentResult,
  type FogbugzToAdoCaseBundle,
  type FogbugzToAdoCreateBody,
  type FogbugzToAdoCreateResult,
  type FogbugzToAdoDraft,
  type FogbugzToAdoPmAddition,
  type FogbugzToAdoSeverity,
  type FogbugzToAdoSlotVerdict,
} from "@ttc-spectree/toolbox-contract";
import { AdoContent } from "@/components/ado/ado-content";
import { Chip } from "@/components/grooming/grooming-chips";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useCreateFogbugzToAdoBug, useFogbugzToAdoClassification } from "@/hooks/queries/use-fogbugz-to-ado";
import { ApiError } from "@/lib/api/client";
import {
  areaOptions,
  attachmentTooLarge,
  barModeOf,
  copyDraft,
  defaultAttachmentIndexes,
  iterationOptions,
  sizeLabel,
  stepsToText,
  textToSteps,
} from "@/lib/toolbox/fogbugz-to-ado";
import { cn } from "@/lib/utils";
import { Notice, Waiting, errorText } from "./parts";

/**
 * The review step of FogBugz to ADO (epic PI-SPEC-SW-E311, task
 * PI-SPEC-SW-1640-4, Resolved Decision 9): Claude's draft, editable, with the
 * Bug exactly as ADO will get it beside it (composed by the contract's
 * `composeFogbugzToAdoBug`, the composer SpecTree's write uses), the area
 * path (prefilled from the FogBugz ↔ ADO link only, editable) and the
 * iteration (never preselected; first "<project> (no sprint)", then the
 * sprints), the files to copy, and one Create. Create is the only write and
 * sends exactly the draft the preview shows.
 */

const fieldClass = cn(
  "w-full rounded-md border border-[#3F3F46] bg-[#111113] px-3 py-2 text-[13px] leading-relaxed outline-none",
  "focus:border-[#66A1FF] focus:ring-2 focus:ring-[#66A1FF]/25",
);

const selectClass = cn(fieldClass, "h-9 py-0");

/** One labelled text box of the draft. */
function TextField({
  id,
  label,
  value,
  onChange,
  max,
  rows = 3,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  max: number;
  rows?: number;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-xs font-medium text-muted-foreground">
        {label}
      </label>
      <textarea
        id={id}
        rows={rows}
        value={value}
        maxLength={max}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        className={fieldClass}
      />
    </div>
  );
}

/** The duplicate a 409 names, if any. Pure; exported for tests. */
export function duplicateOf(error: unknown): { id: number; url: string } | null {
  if (!(error instanceof ApiError)) return null;
  const details = (error.data as { error?: { details?: { existing?: { id?: unknown; url?: unknown } } } } | undefined)?.error
    ?.details;
  const existing = details?.existing;
  return typeof existing?.id === "number" && typeof existing.url === "string" ? { id: existing.id, url: existing.url } : null;
}

/** The missing Bug-bar items a 400 BELOW_BUG_BAR names, if any. Pure; exported for tests. */
export function missingOf(error: unknown): string[] {
  if (!(error instanceof ApiError)) return [];
  const details = (error.data as { error?: { details?: { missing?: unknown } } } | undefined)?.error?.details;
  if (!Array.isArray(details?.missing)) return [];
  return details.missing
    .map((miss) => (miss && typeof miss === "object" ? (miss as { label?: unknown }).label : undefined))
    .filter((label): label is string => typeof label === "string");
}

export interface ReviewStepProps {
  bundle: FogbugzToAdoCaseBundle;
  pmName: string;
  /** Claude's draft (copied; the review edits its own copy). */
  draft: FogbugzToAdoDraft;
  slots: readonly FogbugzToAdoSlotVerdict[];
  pmAdditions: readonly FogbugzToAdoPmAddition[];
  onCreated: (result: FogbugzToAdoCreateResult) => void;
}

export function ReviewStep({ bundle, pmName, draft: claudeDraft, slots, pmAdditions, onCreated }: ReviewStepProps) {
  const [draft, setDraft] = useState<FogbugzToAdoDraft>(() => copyDraft(claudeDraft));
  // Null until the PM edits the steps: Claude's steps are sent as Claude wrote them, never re-split.
  const [stepsText, setStepsText] = useState<string | null>(null);
  // Routing is never inferred: the area starts from the FogBugz ↔ ADO link only; the iteration starts empty.
  const [areaPath, setAreaPath] = useState(bundle.ado.defaultAreaPath ?? "");
  const [iterationPath, setIterationPath] = useState("");
  const [attachmentIndexes, setAttachmentIndexes] = useState<number[]>(() => defaultAttachmentIndexes(bundle.attachments));
  const classification = useFogbugzToAdoClassification(bundle.ado.project);
  const create = useCreateFogbugzToAdoBug();

  // The draft the preview shows and Create sends: one object.
  const reviewed = useMemo<FogbugzToAdoDraft>(
    () => (stepsText === null ? draft : { ...draft, reproSteps: textToSteps(stepsText) }),
    [draft, stepsText],
  );
  const composed = useMemo(
    () => composeFogbugzToAdoBug({ draft: reviewed, caseId: bundle.caseId, caseUrl: bundle.caseUrl, pmName }),
    [reviewed, bundle.caseId, bundle.caseUrl, pmName],
  );
  const body: FogbugzToAdoCreateBody = {
    draft: reviewed,
    confirmedSlots: [...slots],
    areaPath,
    iterationPath,
    attachmentIndexes: [...attachmentIndexes].sort((a, b) => a - b),
    barMode: barModeOf(pmAdditions),
    pmAdditions: [...pmAdditions],
  };
  const validity = fogbugzToAdoCreateBodySchema.safeParse(body);
  const draftProblems = validity.success
    ? []
    : [...new Set(validity.error.issues.filter((issue) => issue.path[0] === "draft").map((issue) => draftProblem(issue.path)))];
  const areas = areaOptions(classification.data, bundle.ado.defaultAreaPath);
  const iterations = iterationOptions(bundle.ado.project, classification.data?.iterations ?? []);
  const canCreate = areaPath !== "" && iterationPath !== "" && validity.success && !create.isPending;

  const update = (patch: Partial<FogbugzToAdoDraft>) => {
    setDraft((current) => ({ ...current, ...patch }));
  };

  const submit = () => {
    if (!canCreate) return;
    create.mutate(
      { caseId: bundle.caseId, project: bundle.ado.project, body },
      {
        onSuccess: (result) => {
          onCreated(result);
        },
      },
    );
  };

  const duplicate = duplicateOf(create.error);
  const stillMissing = missingOf(create.error);

  return (
    <section className="flex flex-col gap-4" aria-labelledby="fogbugz-to-ado-review" data-testid="fogbugz-to-ado-review">
      <div className="flex flex-wrap items-center gap-2">
        <h2 id="fogbugz-to-ado-review" className="font-display text-base font-semibold">
          Review the Bug for {bundle.ado.project}
        </h2>
        <Chip tone="cur">Not created yet</Chip>
      </div>
      <p className="text-[13px] text-muted-foreground">
        Edit anything Claude wrote. The preview is exactly what ADO gets; nothing is created until you click Create.
      </p>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="flex flex-col gap-3" data-testid="fogbugz-to-ado-draft-fields">
          <div className="flex flex-col gap-1">
            <label htmlFor="fogbugz-to-ado-title" className="text-xs font-medium text-muted-foreground">
              Title
            </label>
            <Input
              id="fogbugz-to-ado-title"
              value={draft.title}
              maxLength={FOGBUGZ_TO_ADO_TITLE_MAX}
              onChange={(event) => {
                update({ title: event.target.value });
              }}
            />
          </div>
          <TextField
            id="fogbugz-to-ado-steps"
            label="Steps to reproduce (one per line)"
            value={stepsText ?? stepsToText(draft.reproSteps)}
            onChange={setStepsText}
            max={FOGBUGZ_TO_ADO_TEXT_SECTION_MAX * 5}
            rows={5}
          />
          <TextField id="fogbugz-to-ado-expected" label="Expected" value={draft.expected} onChange={(v) => { update({ expected: v }); }} max={FOGBUGZ_TO_ADO_TEXT_SECTION_MAX} />
          <TextField id="fogbugz-to-ado-actual" label="Actual" value={draft.actual} onChange={(v) => { update({ actual: v }); }} max={FOGBUGZ_TO_ADO_TEXT_SECTION_MAX} />
          <TextField
            id="fogbugz-to-ado-environment"
            label="Environment"
            value={draft.environment}
            onChange={(v) => {
              update({ environment: v });
            }}
            max={FOGBUGZ_TO_ADO_TEXT_SECTION_MAX}
            rows={2}
          />
          <div className="flex flex-col gap-1">
            <label htmlFor="fogbugz-to-ado-severity" className="text-xs font-medium text-muted-foreground">
              Severity
            </label>
            <select
              id="fogbugz-to-ado-severity"
              value={draft.severity}
              onChange={(event) => {
                update({ severity: event.target.value as FogbugzToAdoSeverity });
              }}
              className={selectClass}
            >
              {FOGBUGZ_TO_ADO_SEVERITIES.map((severity) => (
                <option key={severity} value={severity}>
                  {severity}
                </option>
              ))}
            </select>
          </div>
          {draft.evtxSummaries.map((summary, index) => (
            <TextField
              key={`evtx-${summary.fileName}-${String(index)}`}
              id={`fogbugz-to-ado-evtx-${String(index)}`}
              label={`Event log summary: ${summary.fileName}`}
              value={summary.text}
              onChange={(v) => {
                update({
                  evtxSummaries: draft.evtxSummaries.map((item, i) => (i === index ? { ...item, text: v } : item)),
                });
              }}
              max={FOGBUGZ_TO_ADO_TEXT_SECTION_MAX}
              rows={4}
            />
          ))}
          {draft.screenshotDescriptions.map((description, index) => (
            <TextField
              key={`shot-${description.fileName}-${String(index)}`}
              id={`fogbugz-to-ado-shot-${String(index)}`}
              label={`What ${description.fileName} shows`}
              value={description.description}
              onChange={(v) => {
                update({
                  screenshotDescriptions: draft.screenshotDescriptions.map((item, i) =>
                    i === index ? { ...item, description: v } : item,
                  ),
                });
              }}
              max={FOGBUGZ_TO_ADO_TEXT_SECTION_MAX}
            />
          ))}
          <TextField
            id="fogbugz-to-ado-history"
            label="Case history (digest)"
            value={draft.historyDigest}
            onChange={(v) => {
              update({ historyDigest: v });
            }}
            max={FOGBUGZ_TO_ADO_TEXT_SECTION_MAX}
            rows={4}
          />
          {draftProblems.length > 0 && (
            <Notice tone="warn" testId="fogbugz-to-ado-draft-problems">
              <p>Fix these before you create the Bug:</p>
              <ul className="list-disc pl-5">
                {draftProblems.map((problem) => (
                  <li key={problem}>{problem}</li>
                ))}
              </ul>
            </Notice>
          )}
        </div>

        <div className="flex flex-col gap-2" data-testid="fogbugz-to-ado-preview">
          <span className="text-xs font-medium text-muted-foreground">How it looks in ADO</span>
          <div className="flex flex-col gap-3 rounded-md border border-white/[0.08] bg-white/[0.02] p-4 text-sm">
            <p className="font-display text-[15px] font-semibold" data-testid="fogbugz-to-ado-preview-title">
              {composed.title}
            </p>
            <div className="flex flex-col gap-1">
              <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#A1A1AA]">Repro Steps</span>
              <AdoContent html={composed.reproStepsHtml} tone="dark" testId="fogbugz-to-ado-preview-repro" />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#A1A1AA]">System Info</span>
              <AdoContent html={composed.systemInfoHtml} tone="dark" testId="fogbugz-to-ado-preview-system" />
            </div>
            <p className="text-xs text-muted-foreground">
              Severity {reviewed.severity} · Tag {composed.tags.join(", ")}
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1">
          <label htmlFor="fogbugz-to-ado-area" className="text-xs font-medium text-muted-foreground">
            Area path
          </label>
          <select
            id="fogbugz-to-ado-area"
            value={areaPath}
            disabled={classification.isPending}
            onChange={(event) => {
              setAreaPath(event.target.value);
            }}
            className={selectClass}
            data-testid="fogbugz-to-ado-area"
          >
            <option value="" disabled>
              {classification.isPending ? "Loading area paths…" : "Choose an area path"}
            </option>
            {areas.map((path) => (
              <option key={path} value={path}>
                {path}
              </option>
            ))}
          </select>
          {bundle.ado.defaultAreaPath && (
            <span className="text-xs text-muted-foreground">Suggested by the FogBugz project's link; change it if needed.</span>
          )}
          {classification.data?.truncated === true && (
            <span className="text-xs text-muted-foreground" data-testid="fogbugz-to-ado-area-cut">
              The list is cut: {bundle.ado.project} has more area paths than SpecTree lists.
            </span>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="fogbugz-to-ado-iteration" className="text-xs font-medium text-muted-foreground">
            Iteration
          </label>
          <select
            id="fogbugz-to-ado-iteration"
            value={iterationPath}
            disabled={classification.isPending}
            onChange={(event) => {
              setIterationPath(event.target.value);
            }}
            className={selectClass}
            data-testid="fogbugz-to-ado-iteration"
          >
            <option value="" disabled>
              {classification.isPending ? "Loading iterations…" : "Choose an iteration"}
            </option>
            {iterations.map((option) => (
              <option key={option.path} value={option.path}>
                {option.label}
              </option>
            ))}
          </select>
          {classification.data?.iterationsTruncated === true && (
            <span className="text-xs text-muted-foreground" data-testid="fogbugz-to-ado-iteration-cut">
              The list is cut: newest first, the oldest iterations of {bundle.ado.project} are left out.
            </span>
          )}
        </div>
      </div>
      {classification.isError && (
        <Notice tone="error" testId="fogbugz-to-ado-classification-error">
          <p>{errorText(classification.error, `Could not read the area paths and iterations of ${bundle.ado.project}.`)}</p>
        </Notice>
      )}

      {bundle.attachments.length > 0 && (
        <fieldset className="flex flex-col gap-1.5" data-testid="fogbugz-to-ado-attachments">
          <legend className="mb-1 text-xs font-medium text-muted-foreground">Files to copy from the case to the Bug</legend>
          {bundle.attachments.map((attachment) => {
            const tooLarge = attachmentTooLarge(attachment);
            const checked = attachmentIndexes.includes(attachment.index);
            const Icon = attachment.kind === "image" ? ImageIcon : attachment.kind === "evtx" ? FileText : Paperclip;
            return (
              <label key={attachment.index} className="flex items-center gap-2 text-[13px]">
                <input
                  type="checkbox"
                  checked={checked && !tooLarge}
                  disabled={tooLarge}
                  onChange={(event) => {
                    const on = event.target.checked;
                    setAttachmentIndexes((current) =>
                      on ? [...current, attachment.index] : current.filter((index) => index !== attachment.index),
                    );
                  }}
                />
                <Icon className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                <span className="truncate">{attachment.fileName}</span>
                <span className="text-xs text-muted-foreground">{sizeLabel(attachment.sizeBytes)}</span>
                {tooLarge && <Chip tone="warn">Too large; stays on FogBugz</Chip>}
              </label>
            );
          })}
        </fieldset>
      )}

      {create.isError && (
        <Notice tone="error" testId="fogbugz-to-ado-create-error">
          <p>{errorText(create.error, "Could not create the Bug. Nothing was created.")}</p>
          {stillMissing.length > 0 && <p>Still needed: {stillMissing.join(", ")}.</p>}
          {duplicate && (
            <a href={duplicate.url} target="_blank" rel="noreferrer" className="text-link hover:underline">
              Open ADO Bug #{duplicate.id}
            </a>
          )}
        </Notice>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <Button type="button" disabled={!canCreate} onClick={submit} data-testid="fogbugz-to-ado-create">
          {create.isPending ? "Creating…" : `Create ADO Bug in ${bundle.ado.project}`}
        </Button>
        {create.isPending && <Waiting label="Creating the Bug and copying the files" />}
        {(areaPath === "" || iterationPath === "") && !create.isPending && (
          <span className="text-xs text-muted-foreground">Choose an area path and an iteration first.</span>
        )}
      </div>
    </section>
  );
}

/** A plain sentence for a draft field the create body would refuse. */
function draftProblem(path: readonly PropertyKey[]): string {
  switch (path[1]) {
    case "title":
      return "Give the Bug a title.";
    case "reproSteps":
      return "Write at least two steps to reproduce, one per line.";
    case "expected":
      return "Say what was expected.";
    case "actual":
      return "Say what actually happened.";
    case "environment":
      return "Describe the environment in at least 10 characters.";
    case "evtxSummaries":
      return "An event log summary is empty.";
    case "screenshotDescriptions":
      return "A screenshot description is empty.";
    default:
      return "Part of the draft is not valid.";
  }
}

// ---------------------------------------------------------------------------
// Result
// ---------------------------------------------------------------------------

const ATTACHMENT_STATUS: Record<FogbugzToAdoAttachmentResult["status"], { text: string; tone: "ok" | "warn" | "none" }> = {
  attached: { text: "Attached", tone: "ok" },
  "too-large": { text: "Too large; stays on the FogBugz case", tone: "warn" },
  failed: { text: "Could not be attached", tone: "warn" },
};

/**
 * "ADO Bug #N created" with its link, each file's outcome and the FogBugz
 * note's, then a warning when the Bug's link back to the case failed and when
 * another copy of the case was found on the board after the create.
 */
export function CreatedResult({ caseId, result }: { caseId: number; result: FogbugzToAdoCreateResult }) {
  const otherCopies = result.otherCopies ?? [];
  return (
    <>
      <Notice tone="ok" testId="fogbugz-to-ado-created">
        <p className="text-sm font-semibold">
          ADO Bug #{result.ado.id} created.{" "}
          <a
            href={result.ado.url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 font-normal text-link hover:underline"
            data-testid="fogbugz-to-ado-created-link"
          >
            Open it in ADO
            <ExternalLink className="h-3 w-3" aria-hidden="true" />
          </a>
        </p>
        {result.attachments.length > 0 && (
          <ul className="flex flex-col gap-1" data-testid="fogbugz-to-ado-created-files">
            {result.attachments.map((file, index) => {
              const status = ATTACHMENT_STATUS[file.status];
              return (
                <li key={`${file.fileName}-${String(index)}`} className="flex flex-wrap items-center gap-2">
                  <span className="truncate">{file.fileName}</span>
                  <Chip tone={status.tone}>{status.text}</Chip>
                  {file.message && <span className="text-xs text-muted-foreground">{file.message}</span>}
                </li>
              );
            })}
          </ul>
        )}
        <p data-testid="fogbugz-to-ado-created-note">
          {result.fogbugzNote === "posted"
            ? `A note with the ADO link was posted to FogBugz case ${String(caseId)}.`
            : `The note to FogBugz case ${String(caseId)} could not be posted; add the ADO link to the case by hand. The Bug was created.`}
        </p>
      </Notice>
      {result.hyperlink === "failed" && (
        <Notice tone="warn" testId="fogbugz-to-ado-created-hyperlink-failed">
          <p>
            The link from ADO Bug #{result.ado.id} back to FogBugz case {caseId} could not be added. Add it to the Bug by hand;
            its fogbugz-{caseId} tag still ties it to the case.
          </p>
        </Notice>
      )}
      {otherCopies.length > 0 && (
        <Notice tone="warn" testId="fogbugz-to-ado-created-other-copies">
          <p>
            Case {caseId} was brought to the ADO board more than once at the same time. Nothing was deleted; keep one and close
            the {otherCopies.length === 1 ? "other" : "others"}:
          </p>
          <ul className="flex flex-col gap-1">
            {otherCopies.map((copy) => (
              <li key={copy.id}>
                <a href={copy.url} target="_blank" rel="noreferrer" className="text-link hover:underline">
                  ADO Bug #{copy.id}
                </a>
              </li>
            ))}
          </ul>
        </Notice>
      )}
    </>
  );
}
